<template>
	<TeamingDialog
		:className="classes"
		:showDialog="showDialog"
		v-on="bubble( `hide` )"
	>
		<template #header>
			<div class="individual-dialog__header">
				<div>
					<AssessmentHeader
						:user="user"
						:assessment="assessment"
					/>
					<FontAwesomeIcon
						class="individual-dialog__close-button"
						:icon="[ `fas`, `times` ]"
						@click.stop="$emit( `hide` )"
					/>
				</div>
				<TeamingSegmentedButton
					class="individual-dialog__segment-button"
					:active-segment-name="activeSegment"
					left-segment-name="personal-details"
					right-segment-name="work-style"
					@change="onSegmentChange"
				>
					<template #left-segment>
						{{ translate( "personalDetailsButton", { name: user.shortDisplayName } ) }}
					</template>
					<template #right-segment>
						{{ translate( "workStyleButton" ) }}
					</template>
				</TeamingSegmentedButton>
			</div>
		</template>

		<section class="individual-dialog__body">
			<section :class="personalDetailsSegmentClasses" >
				<h2>{{ translate( "personalDetailsHeading", { name: user.displayName } ) }}</h2>
				<TeammatePersonalDetails
					:assessment="assessment"
					:user="user"
				/>
			</section>
			<section :class="workStyleSegmentClasses">
				<h2>{{ translate( "workStyleHeading" ) }}</h2>
				<div class="individual-dialog__workstyle">
					<TeammateDISCProfile
						:user="user"
					/>
				</div>
			</section>
		</section>
	</TeamingDialog>
</template>

<script>
import {
	TranslateMixin,
	EventsMixin,
	TeamingDialog,
	AssessmentHeader,
	TeamingSegmentedButton
} from "@teaming/common-ui";

import TeammatePersonalDetails from "./IndividualPersonalDetails";
import TeammateDISCProfile from "./IndividualDISCProfile";

export default {
	name: "IndividualDialog",
	teamingPackage: "common-team",
	emits: [ "hide", ],
	mixins: [ TranslateMixin, EventsMixin, ],
	components: {
		TeamingDialog,
		AssessmentHeader,
		TeamingSegmentedButton,
		TeammatePersonalDetails,
		TeammateDISCProfile,
	},
	props: {
		className: {
			type: String,
			default: "",
		},
		user: {
			type: Object,
			required: true,
		},
		showDialog: {
			type: Boolean,
			required: true,
		},
	},
	data() {
		return {
			activeSegment: "work-style",
		};
	},
	computed: {
		classes() {
			return `individual-dialog ${ this.className }`.trim();
		},
		personalDetailsSegmentClasses() {
			return {
				"individual-dialog__segment": true,
				"individual-dialog__segment--active": this.activeSegment === "personal-details",
				"individual-dialog__personal-details-segment": true,
			};
		},
		workStyleSegmentClasses() {
			return {
				"individual-dialog__segment": true,
				"individual-dialog__segment--active": this.activeSegment === "work-style",
				"individual-dialog__work-style-segment": true,
			};
		},
		assessment() {
			return this.user?.assessments?.DISC || {};
		},
	},
	methods: {
		onSegmentChange( activeSegment ) {
			this.activeSegment = activeSegment;
		},
	},
};
</script>

<style lang="scss">
.individual-dialog.p-dialog {
	width: 30rem;
	max-width: 30rem;
	min-width: 30rem;
}
</style>

<style lang="scss" scoped>
.individual-dialog__header {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	width: 100%;

	div:first-child {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
	}
}

.individual-dialog__body {
	width: 100%;
	position: relative;
	margin-top: 1rem;
}

.individual-dialog__segment {
	display: none;
	flex-direction: column;
	gap: 1rem;

	max-width: 35rem;
	min-height: 30rem;
	max-height: 30rem;

	> h2 {
		display: none;
		@include font-and-line-for(1);
		font-weight: 600;
		color: $individual-dialog-header-color;
		text-align: left;
	}
}

.individual-dialog__segment--active {
	display: flex;
}

.individual-dialog__personal-details-segment {
	> * {
		width: 100%;
	}
}

.individual-dialog__workstyle {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: flex-start;
}

.individual-dialog__close-button {
	cursor: pointer;
}
</style>
